/**
 * stallWatchdog — makes main-thread freezes VISIBLE in the error log.
 *
 * Why (2026-08-26): a device report of "tapped Calendar and the app froze"
 * left no trace anywhere — a frozen main thread can't run the error reporter,
 * and a JS exception would have hit RootErrorBoundary instead. This watchdog
 * turns the two invisible failure shapes into /api/errors rows:
 *
 *  1. RECOVERED STALLS — a 1s heartbeat measures timer drift. When a tick
 *     lands ≥ STALL_REPORT_MS late while the page is visible, the main thread
 *     was blocked that long (long synchronous render, OCR on the main thread,
 *     a pathological loop that eventually finished) and we report it with the
 *     route where it happened.
 *
 *  2. FROZEN-THEN-KILLED SESSIONS — every heartbeat persists {route, ts} to
 *     localStorage; `pagehide` marks the record as a clean exit and `pageshow`
 *     re-arms it. A session that ends WITHOUT pagehide (the page was frozen,
 *     so no JS — including pagehide — could run before the user killed the
 *     app) leaves an unmarked record, which the NEXT launch reports along
 *     with the route the freeze happened on. iOS fires pagehide on plain
 *     backgrounding, so a normal swipe-kill from the app switcher does NOT
 *     false-positive here.
 *
 * Suspension guard: backgrounding also pauses timers, which looks exactly
 * like drift on resume. Any visibilitychange/pageshow/pagehide resets the
 * heartbeat baseline, and a drift window that overlaps a visibility
 * transition is discarded rather than reported.
 */

export interface StallReport {
  message: string;
  route?: string;
  level?: "error" | "warn";
}

const TICK_MS = 1_000;
/** Main-thread block long enough to report — matches "the app froze" as a
 * trainer would describe it, while staying above GC/paint hiccups. */
const STALL_REPORT_MS = 4_000;
const STORAGE_KEY = "nexus.stallWatchdog.v1";

interface PersistedBeat {
  ts: number;
  route: string;
  cleanExit?: boolean;
}

function currentRoute(): string {
  if (typeof window === "undefined") return "";
  return window.location.hash || window.location.pathname || "";
}

function readPersisted(): PersistedBeat | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedBeat;
    return typeof parsed?.ts === "number" ? parsed : null;
  } catch {
    return null;
  }
}

function writePersisted(beat: PersistedBeat): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(beat));
  } catch {
    /* storage full / private mode — the watchdog degrades to stalls only */
  }
}

/**
 * Install the watchdog. Returns an uninstall function. `post` is the existing
 * /api/errors reporter (already throttled per-message there).
 */
export function installStallWatchdog(post: (p: StallReport) => void): () => void {
  if (typeof window === "undefined") return () => {};

  // ── 2) Report the PREVIOUS session's freeze-then-kill, if any. ───────────
  const previous = readPersisted();
  if (previous && !previous.cleanExit) {
    post({
      level: "warn",
      message: `Previous session ended without pagehide on ${previous.route || "unknown route"} — page was likely frozen or crashed when the app was killed`,
      route: previous.route,
    });
  }

  // ── 1) Heartbeat: drift detection + persisted breadcrumb. ────────────────
  let lastTick = performance.now();
  // The route as of the LAST healthy tick — a stall is reported against where
  // the page was when the block began, not where a queued navigation landed.
  let lastRoute = currentRoute();
  // -Infinity, not 0: no visibility transition has happened yet, and a 0 would
  // make the suspension guard swallow any stall in the first seconds after
  // install (install time ≈ performance.now() 0 at app boot).
  let lastVisibilityEventAt = Number.NEGATIVE_INFINITY;

  const resetBaseline = () => {
    lastVisibilityEventAt = performance.now();
    lastTick = performance.now();
    lastRoute = currentRoute();
  };

  const interval = window.setInterval(() => {
    const now = performance.now();
    const drift = now - lastTick - TICK_MS;
    const routeAtStall = lastRoute;
    lastTick = now;
    lastRoute = currentRoute();
    writePersisted({ ts: Date.now(), route: lastRoute });

    if (drift < STALL_REPORT_MS) return;
    if (document.visibilityState !== "visible") return;
    // A visibility transition inside (or right around) the drift window means
    // the timer was SUSPENDED, not blocked — resume, don't report.
    if (now - lastVisibilityEventAt < drift + 2 * TICK_MS) return;
    post({
      level: "warn",
      message: `Main thread stalled ~${Math.round(drift / 1000)}s on ${routeAtStall || "unknown route"}`,
      route: routeAtStall,
    });
  }, TICK_MS);

  const onPageHide = () => {
    writePersisted({ ts: Date.now(), route: currentRoute(), cleanExit: true });
    resetBaseline();
  };
  const onPageShow = () => {
    // Session continues — re-arm the abnormal-exit breadcrumb.
    writePersisted({ ts: Date.now(), route: currentRoute() });
    resetBaseline();
  };
  const onVisibility = () => resetBaseline();

  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.clearInterval(interval);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

/** Test-only: the storage key + thresholds, so tests need no magic strings. */
export const __stallWatchdogInternals = { STORAGE_KEY, TICK_MS, STALL_REPORT_MS };
